// frontend-react/src/pages/ChangePasswordPage.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Button, Card, Form, Input, Typography, message, Progress, Tag } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { getApiBaseUrl } from '../config/api';

const { Title, Text } = Typography;

type PasswordPolicy = {
  min_length: number;
  required_classes: number;
  rotation_days: number;
  allowed_special_chars: string;
  classes?: string[];
  note?: string;
};

type PasswordCheckResult = {
  lenOK: boolean;
  classesOK: boolean;
  classesCount: number;
  hasUpper: boolean;
  hasLower: boolean;
  hasDigit: boolean;
  hasSpecial: boolean;
};

const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  min_length: 14,
  required_classes: 3,
  allowed_special_chars: '!@#$%^&*()_-+=[]{};\':"\\|,.<>/?',
  rotation_days: 90,
};

// 產生「只允許常見英文半形特殊符號」的 regex（跟後端 allowed_special_chars 同步）
function buildAllowedSpecialRegex(allowedChars: string): RegExp {
  const escaped = allowedChars.replace(/[-\\\]^]/g, '\\$&');
  return new RegExp(`[${escaped}]`);
}

// 純前端即時檢核（不打 API）
function checkPasswordLocal(
  pwd: string,
  policy: PasswordPolicy,
  specialRegex: RegExp
): PasswordCheckResult {
  const hasUpper = /[A-Z]/.test(pwd);
  const hasLower = /[a-z]/.test(pwd);
  const hasDigit = /[0-9]/.test(pwd);
  const hasSpecial = specialRegex.test(pwd);

  const classesCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length;

  return {
    lenOK: pwd.length >= policy.min_length,
    classesOK: classesCount >= policy.required_classes,
    classesCount,
    hasUpper,
    hasLower,
    hasDigit,
    hasSpecial,
  };
}

function getMissingClasses(r: PasswordCheckResult) {
  const missing: string[] = [];
  if (!r.hasUpper) missing.push('大寫');
  if (!r.hasLower) missing.push('小寫');
  if (!r.hasDigit) missing.push('數字');
  if (!r.hasSpecial) missing.push('符號');
  return missing;
}

const ChangePasswordPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [policy, setPolicy] = useState<PasswordPolicy>(DEFAULT_PASSWORD_POLICY);
  const [submitting, setSubmitting] = useState(false);

  // 即時檢核用 state
  const [passwordLive, setPasswordLive] = useState('');
  const [confirmLive, setConfirmLive] = useState('');

  const specialRegex = useMemo(
    () => buildAllowedSpecialRegex(policy.allowed_special_chars),
    [policy.allowed_special_chars]
  );

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const resp = await fetch(`${getApiBaseUrl()}/api/auth/password-policy`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (
          typeof data?.min_length === 'number' &&
          typeof data?.required_classes === 'number' &&
          typeof data?.allowed_special_chars === 'string' &&
          typeof data?.rotation_days === 'number'
        ) {
          setPolicy(data);
        }
      } catch {
        // policy 只是提示，失敗不影響功能
      }
    };
    fetchPolicy();
  }, []);

  const onFinish = async (values: {
    oldPassword: string;
    newPassword: string;
    confirm: string;
  }) => {
    setSubmitting(true);
    try {
      if (values.newPassword !== values.confirm) {
        throw new Error('兩次輸入的新密碼不一致');
      }

      await authService.changePassword(values.oldPassword, values.newPassword);

      message.success('密碼已更新，請重新登入');
      await authService.logout();
      navigate('/login', { replace: true });
    } catch (e: any) {
      message.error(e?.message || '變更密碼失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const r = checkPasswordLocal(passwordLive, policy, specialRegex);
  const missing = getMissingClasses(r);
  const basicPass = r.lenOK && r.classesOK;
  const confirmMismatch = Boolean(confirmLive) && confirmLive !== passwordLive;
  const canSubmitHint = basicPass && !confirmMismatch;

  // 進度條：把長度當 1 分，類型達標當 1 分（總分 2）
  const score = (r.lenOK ? 1 : 0) + (r.classesOK ? 1 : 0);
  const percent = Math.round((score / 2) * 100);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'var(--color-bg)',
        padding: 16,
      }}
    >
      <Card style={{ width: 520 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <LockOutlined style={{ fontSize: 48, color: '#1E88A8' }} />
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
            請先更新密碼
          </Title>
        </div>

        <Alert
          type='warning'
          showIcon
          message='您的密碼已過期（90天）'
          description='基於安全政策，每 90 天需更新密碼，請先變更密碼後才能繼續使用系統。'
          style={{ marginBottom: 16 }}
        />

        {/* 規則提醒（由 policy 生成） */}
        <Alert
          message='密碼要求'
          description={
            <div>
              <div>• 密碼長度：至少 {policy.min_length} 字元</div>
              <div>• 至少包含 {policy.required_classes} 項：大寫 / 小寫 / 數字 / 符號</div>
              <div>• 每 {policy.rotation_days} 天需更換一次密碼</div>
              <div style={{ color: '#666' }}>※ 限制：密碼不可等於或包含於帳號內</div>
            </div>
          }
          type='info'
          showIcon
          style={{ marginBottom: 12 }}
        />

        {/* 即時檢核 */}
        {passwordLive ? (
          <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 10 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ fontWeight: 600 }}>
                密碼檢核：{canSubmitHint ? '✅ 通過' : '⚠ 尚未通過'}
                {!canSubmitHint && missing.length > 0 ? `（缺少：${missing.join('、')}）` : ''}
              </div>
              <div style={{ minWidth: 160 }}>
                <Progress percent={percent} size='small' />
              </div>
            </div>

            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Tag color={r.lenOK ? 'green' : 'red'}>
                長度 ≥ {policy.min_length} {r.lenOK ? '✓' : '✕'}
              </Tag>
              <Tag color={r.classesOK ? 'green' : 'red'}>
                類型 {policy.required_classes}/4（{r.classesCount}/4）{r.classesOK ? '✓' : '✕'}
              </Tag>
              <Tag color={r.hasUpper ? 'green' : 'default'}>大寫 {r.hasUpper ? '✓' : '✕'}</Tag>
              <Tag color={r.hasLower ? 'green' : 'default'}>小寫 {r.hasLower ? '✓' : '✕'}</Tag>
              <Tag color={r.hasDigit ? 'green' : 'default'}>數字 {r.hasDigit ? '✓' : '✕'}</Tag>
              <Tag color={r.hasSpecial ? 'green' : 'default'}>符號 {r.hasSpecial ? '✓' : '✕'}</Tag>
            </div>

            {confirmMismatch && (
              <div style={{ marginTop: 10, color: '#cf1322' }}>❌ 兩次輸入的密碼不一致</div>
            )}
          </div>
        ) : null}

        <Form
          form={form}
          layout='vertical'
          onFinish={onFinish}
          onValuesChange={(_, allValues) => {
            setPasswordLive(allValues.newPassword || '');
            setConfirmLive(allValues.confirm || '');
          }}
        >
          <Form.Item
            name='oldPassword'
            label='舊密碼'
            rules={[{ required: true, message: '請輸入舊密碼' }]}
          >
            <Input.Password prefix={<LockOutlined />} size='large' placeholder='請輸入目前的密碼' />
          </Form.Item>

          <Form.Item
            name='newPassword'
            label='新密碼'
            rules={[
              { required: true, message: '請輸入新密碼' },
              () => ({
                validator(_, value) {
                  const v = String(value || '');
                  if (!v) return Promise.resolve();

                  const rr = checkPasswordLocal(v, policy, specialRegex);
                  if (!rr.lenOK)
                    return Promise.reject(new Error(`密碼至少需 ${policy.min_length} 字元`));
                  if (!rr.classesOK)
                    return Promise.reject(
                      new Error(
                        `需包含至少 ${policy.required_classes} 種字元類型（大寫/小寫/數字/英文符號）`
                      )
                    );
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              size='large'
              placeholder={`請輸入至少 ${policy.min_length} 個字元`}
            />
          </Form.Item>

          <Form.Item
            name='confirm'
            label='確認新密碼'
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '請再次輸入新密碼' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('兩次輸入的密碼不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} size='large' placeholder='再次輸入新密碼' />
          </Form.Item>

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              type='primary'
              htmlType='submit'
              size='large'
              block
              loading={submitting}
              disabled={!canSubmitHint}
            >
              變更密碼
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Text type='secondary' style={{ fontSize: 12 }}>
            變更完成後，請使用新密碼重新登入系統
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default ChangePasswordPage;
